import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { AuthLayout } from "@/components/app/AuthLayout";
import { TextField, PrimaryButton } from "@/components/app/TextField";
import { GoogleButton } from "@/components/app/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { apiError, publicApi } from "@/lib/api";
import { AUTH } from "@/constants/testIds";

function Checkbox({ testId, checked, onChange, children }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-brand-sec">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-accent"
      />
      <span>{children}</span>
    </label>
  );
}

function safeReturnTo(value) {
  if (!value) return "/biblioteka";
  if (!value.startsWith("/") || value.startsWith("//")) return "/biblioteka";
  return value;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get("returnTo"));

  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bonus, setBonus] = useState(null);

  useEffect(() => {
    publicApi.welcomeBonus().then((b) => { if (b?.enabled) setBonus(b); }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Hasło musi mieć co najmniej 8 znaków.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Hasła nie są identyczne.");
      return;
    }
    if (!terms || !privacy) {
      setError("Wymagana akceptacja regulaminu i polityki prywatności.");
      return;
    }
    setLoading(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        first_name: form.name || null,
        accept_terms: terms,
        accept_privacy: privacy,
        newsletter,
      });
      toast.success("Konto utworzone. Sprawdź pocztę, aby potwierdzić e-mail.");
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Załóż konto"
      subtitle="Utwórz konto Promptivo. Dostęp do pakietów aktywuje się po zakupie."
      footer={
        <span>
          Masz już konto?{" "}
          <Link
            to="/logowanie"
            data-testid={AUTH.loginLink}
            className="font-medium text-brand-accent hover:underline"
          >
            Zaloguj się
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {bonus && (
          <div data-testid="register-welcome-bonus" className="flex items-start gap-2.5 rounded-field border border-brand-accent/40 bg-brand-accent/10 px-4 py-3 text-sm text-brand-text">
            <Gift size={18} className="mt-0.5 shrink-0 text-brand-accent" />
            <span>{bonus.message}</span>
          </div>
        )}
        {error && (
          <div
            data-testid={AUTH.error}
            className="rounded-field border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          >
            {error}
          </div>
        )}
        <TextField label="Imię" testId={AUTH.nameInput} value={form.name} onChange={set("name")} placeholder="Twoje imię" autoComplete="given-name" />
        <TextField label="E-mail" type="email" testId={AUTH.emailInput} value={form.email} onChange={set("email")} placeholder="ty@firma.pl" autoComplete="email" required />
        <TextField label="Hasło" type="password" testId={AUTH.passwordInput} value={form.password} onChange={set("password")} placeholder="Minimum 8 znaków" hint="Minimum 8 znaków." autoComplete="new-password" required />
        <TextField label="Powtórz hasło" type="password" testId={AUTH.passwordConfirmInput} value={form.confirm} onChange={set("confirm")} placeholder="Powtórz hasło" autoComplete="new-password" required />
        <div className="space-y-3 pt-1">
          <Checkbox testId={AUTH.termsCheckbox} checked={terms} onChange={setTerms}>
            Akceptuję <Link to="/regulamin" className="text-brand-accent hover:underline">regulamin</Link>.
          </Checkbox>
          <Checkbox testId={AUTH.privacyCheckbox} checked={privacy} onChange={setPrivacy}>
            Potwierdzam zapoznanie się z <Link to="/polityka-prywatnosci" className="text-brand-accent hover:underline">polityką prywatności</Link>.
          </Checkbox>
          <Checkbox testId={AUTH.newsletterCheckbox} checked={newsletter} onChange={setNewsletter}>
            Chcę otrzymywać newsletter (opcjonalnie).
          </Checkbox>
        </div>
        <PrimaryButton type="submit" data-testid={AUTH.submitButton} disabled={loading}>
          {loading ? "Tworzenie konta…" : "Załóż konto"}
        </PrimaryButton>
      </form>
      <div className="my-6 flex items-center gap-4">
        <div className="h-px flex-1 bg-brand-line" />
        <span className="text-xs uppercase tracking-wide text-brand-muted">lub</span>
        <div className="h-px flex-1 bg-brand-line" />
      </div>
      <GoogleButton testId={AUTH.googleButton} label="Zarejestruj się przez Google" />
    </AuthLayout>
  );
}
